import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFormTextField from '../../../../components/designe/app-form-text-field';
import AppButton from '../../../../components/designe/app-button/AppButton';
import AppFormSelectWrapper from '../../../../components/designe/app-form-select-wrapper';
import { useSyncRuleForm } from './useSyncRuleForm';
import {
  SyncRuleDto,
  SyncRuleTransform,
  useGetSyncProviders,
} from '../../../../services/redux/api/syncApi';
import { ENTITY_TO_TAB } from '../../sync/syncEntityConfig';

type FormValues = {
  provider: string;
  entity: string;
  sourcePath?: string | null;
  defaultValue?: string | null;
  localPath: string;
  prefix?: string | null;
  transform?: SyncRuleTransform | null;
};

interface AddSyncRuleProps {
  rule?: SyncRuleDto | null;
  onClose: () => void;
  onSubmit: (values: FormValues) => void;
}

export function AddSyncRule({ rule, onClose, onSubmit }: AddSyncRuleProps) {
  const { t } = useTranslation();
  const { data: providers } = useGetSyncProviders();
  const { formik, isSubmitting } = useSyncRuleForm(rule, onClose, onSubmit);

  const providerOptions = useMemo(
    () =>
      (providers ?? []).map((p) => ({
        id: p.provider,
        name: p.provider,
      })),
    [providers],
  );

  const entityOptions = useMemo(() => {
    const selectedProvider = providers?.find(
      (p) => p.provider === formik.values.provider,
    );
    const entities = selectedProvider?.entities ?? [];
    return entities.map((entity) => ({
      id: entity,
      name: ENTITY_TO_TAB[entity] ? t(ENTITY_TO_TAB[entity].labelKey) : entity,
    }));
  }, [providers, formik.values.provider, t]);

  const transformOptions: { id: SyncRuleTransform; name: string }[] = [
    { id: 'none', name: 'none' },
    { id: 'toLowerCase', name: 'toLowerCase' },
    { id: 'toUpperCase', name: 'toUpperCase' },
  ];

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={1}>
          <Grid item sm={12}>
            <AppFormSelectWrapper
              label={t('adminDashboard.sync.provider')}
              name="provider"
              options={providerOptions}
              defaultValue={providerOptions.find(
                (opt) => opt.id === formik.values.provider,
              )}
              onChange={(e) => {
                const newProvider = e.target.value.id;
                const providerData = providers?.find(
                  (p) => p.provider === newProvider,
                );
                const validEntity = providerData?.entities.includes(
                  formik.values.entity,
                )
                  ? formik.values.entity
                  : (providerData?.entities[0] ?? '');
                formik.setFieldValue('provider', newProvider);
                formik.setFieldValue('entity', validEntity);
              }}
              formik={formik}
            />
          </Grid>
          <Grid item sm={12}>
            <AppFormSelectWrapper
              label="Entity"
              name="entity"
              options={entityOptions}
              defaultValue={entityOptions.find(
                (opt) => opt.id === formik.values.entity,
              )}
              onChange={(e) =>
                formik.setFieldValue('entity', e.target.value.id)
              }
              formik={formik}
            />
          </Grid>
          <Grid item sm={12}>
            <AppFormTextField
              label="Source path"
              fullWidth
              name="sourcePath"
              formik={formik}
            />
          </Grid>
          <Grid item sm={12}>
            <AppFormTextField
              label="Local path"
              fullWidth
              name="localPath"
              formik={formik}
            />
          </Grid>
          <Grid item sm={12}>
            <AppFormTextField
              label="Default value"
              fullWidth
              name="defaultValue"
              formik={formik}
            />
          </Grid>
          <Grid item sm={12}>
            <AppFormTextField
              label="Prefix"
              fullWidth
              name="prefix"
              formik={formik}
            />
          </Grid>
          <Grid item sm={12}>
            <AppFormSelectWrapper
              label="Transform"
              name="transform"
              options={transformOptions}
              showSearch={false}
              disableClear
              defaultValue={transformOptions.find(
                (opt) => opt.id === formik.values.transform,
              )}
              onChange={(e) =>
                formik.setFieldValue('transform', e.target.value.id)
              }
              formik={formik}
            />
          </Grid>
        </Grid>
        <Box display="flex" justifyContent="flex-end" marginTop={2} gap={1}>
          <AppButton variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </AppButton>
          <AppButton type="submit" loader={isSubmitting}>
            {rule ? t('common.update') : t('common.create')}
          </AppButton>
        </Box>
      </form>
    </Box>
  );
}
